import React, { useState } from 'react';
import { Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { createSubscription } from '../services/stripeService';
import { toast } from '../utils/toast';

interface SubscribeButtonProps {
  calendarId: string;
  isSubscribed: boolean;
  onSubscriptionChange: () => void;
  price: number;
  status?: string;
  expiresAt?: string | null;
}

export default function SubscribeButton({
  calendarId,
  isSubscribed,
  onSubscriptionChange,
  price,
  status,
  expiresAt
}: SubscribeButtonProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Create Stripe Checkout session
      const { url } = await createSubscription({
        calendarId,
        userId: user.id,
        returnUrl: window.location.href
      });

      // Redirect to Stripe Checkout
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (err) {
      console.error('Error subscribing:', err);
      toast.error('Failed to process subscription');
    } finally {
      setLoading(false);
    }
  };

  // Show cancelled status if subscription is cancelled but not yet expired
  if (status === 'canceled' && expiresAt && new Date(expiresAt) > new Date()) {
    return (
      <button
        disabled
        className="w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-gray-700 bg-gray-100 cursor-not-allowed"
      >
        <Users className="h-4 w-4 mr-2" />
        Cancelled
      </button>
    );
  }

  return (
    <button
      onClick={handleSubscribe}
      disabled={loading || isSubscribed}
      className={`w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium ${
        isSubscribed
          ? 'text-gray-700 bg-gray-100 cursor-not-allowed'
          : 'text-white bg-purple-600 hover:bg-purple-700'
      } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50`}
    >
      <Users className="h-4 w-4 mr-2" />
      {loading ? 'Processing...' : isSubscribed ? 'Subscribed' : `Subscribe - $${(price / 100).toFixed(2)}/month`}
    </button>
  );
}